import * as Dialog from '@radix-ui/react-dialog'
import { Cross2Icon } from '@radix-ui/react-icons'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router'
import { z } from 'zod'
import { Routes } from '../../routes.ts'
import { mapToolToLogoAndName } from '../../routes/analysis-details-page'
import { usePostAnalyzeScan, usePostUploadScan } from '../../utils/api-client/api-hooks.ts'
import { CreateScanRequestFormData, Tool } from '../../utils/api-client/api-schemas.ts'
import { useAddToast } from '../../utils/higher-order-components/with-toasts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { InputContainer } from '../input/input-container'
import { SelectBox } from '../input/select-box.tsx'
import { TextInput } from '../input/text-input'
import * as styles from './upload-scan-form-drawer.css.ts'

interface UploadScanFormDrawerProps {
  repositoryId: string
  repositoryDisplayName: string
  handleCloseDrawer: () => void
}
const validTools: Tool[] = [
  Tool.Enum.APPSCAN,
  Tool.Enum.POLARIS,
  Tool.Enum.CHECKMARX,
  Tool.Enum.CODEQL,
  Tool.Enum.SEMGREP,
  Tool.Enum.SNYK,
  Tool.Enum.SONAR,
  Tool.Enum.VERACODE,
]
const tools: Tool[] = Tool.options.filter(tool => validTools.includes(tool as Tool))

export const UploadScanFormDrawer: React.FC<UploadScanFormDrawerProps> = ({ repositoryId, repositoryDisplayName, handleCloseDrawer }) => {
  const navigate = useNavigate()
  const [selectedTool, setSelectedTool] = useState('')
  const [fileName, setFileName] = useState('')
  const [commitHash, setCommitHash] = useState('')

  const analyzeScanMutation = usePostAnalyzeScan({
    onError: (error: any) => {
      handleAddToastWithTimeout({
        message: <>Analysis trigger failed. You can try to analyze it again later. {error.message}.</>,
        variant: 'error',
      })

      if (error.bodyAsText && typeof error.bodyAsText === 'string') {
        handleAddToastWithTimeout({
          message: <pre style={{ margin: '0px' }}>{error.bodyAsText}</pre>,
          variant: 'error',
        })
      }
    },
  })
  const uploadScanMutation = usePostUploadScan({
    onSuccess: ({ id: scanId }) => {
      handleAddToastWithTimeout({
        message: <>Scan uploaded successfully.</>,
        variant: 'success',
      })

      analyzeScanMutation.mutate(
        { scanId },
        {
          onSuccess: ({ id: analysisId }) => navigate(Routes.AnalysisDetailsPage.createPath({ analysisId })),
        }
      )
    },
    onError: (error: any) => {
      handleAddToastWithTimeout({
        message: <>{`Unable to upload scan: ${error.message}. Please try again.`}</>,
        variant: 'error',
      })

      if (error.bodyAsText && typeof error.bodyAsText === 'string') {
        handleAddToastWithTimeout({
          message: <pre style={{ margin: '0px' }}>{error.bodyAsText}</pre>,
          variant: 'error',
        })
      }
    },
  })

  const { handleAddToastWithTimeout } = useAddToast()

  const handleCommitHashChange = useCallback((value: string | number) => {
    setCommitHash(value.toString())
  }, [])

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      const draftFormData = extractData(new FormData(event.currentTarget))
      const { repositoryId, ...verifiedData } = verifyData(draftFormData)
      uploadScanMutation.mutate({ repositoryId, body: verifiedData })
    },
    [uploadScanMutation, selectedTool, repositoryId]
  )

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFileName(event.target.files?.[0]?.name || '')
    } else {
      setFileName('')
    }
  }, [])

  const handleSelectChange = useCallback((value: string) => {
    setSelectedTool(value)
  }, [])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLLabelElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        document.getElementById(`file-${repositoryId}`)?.click()
      }
    },
    [repositoryId]
  )

  const handleClearFile = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      setFileName('')
      const fileInput = document.getElementById(`file-${repositoryId}`) as HTMLInputElement
      if (fileInput) {
        fileInput.value = ''
      }
    },
    [repositoryId]
  )

  return (
    <Dialog.Root open={true} modal={false}>
      <Dialog.Portal>
        <Dialog.Content
          className={styles.content}
          onEscapeKeyDown={event => event.preventDefault()}
          onPointerDownOutside={event => event.preventDefault()}
          onInteractOutside={event => event.preventDefault()}
        >
          <Dialog.Title className={styles.title}>Upload scan results</Dialog.Title>
          <Dialog.Description className={styles.description}>Upload SAST tool results for Pixee to analyze</Dialog.Description>

          <form onSubmit={handleSubmit} className={styles.formContainer}>
            <div className={styles.flexContainer}>
              <input type="hidden" name="repositoryId" value={repositoryId} />
              <TextInput id={`repository-${repositoryId}`} name="repository" label="Repository" value={repositoryDisplayName} disabled />
            </div>

            <div className={styles.flexContainer}>
              <SelectBox
                label="Tool"
                name="tool"
                value={selectedTool}
                onValueChange={handleSelectChange}
                placeholder="Select a tool"
                options={tools
                  .filter(tool => tool != 'PIXEE')
                  .map(tool => {
                    const { name, logoHref } = mapToolToLogoAndName(tool, 'dark')
                    return {
                      value: tool,
                      label: (
                        <span className={styles.selectItemText}>
                          <img src={logoHref} alt={`${name} logo`} width="16" height="16" style={{ marginRight: '8px' }} />
                          {name}
                        </span>
                      ),
                    }
                  })}
              />
            </div>

            <div className={styles.flexContainer}>
              <InputContainer id={`file-${repositoryId}`} label="File" helperText="Results are in SARIF format. Example: cx_results.sarif">
                <input type="file" id={`file-${repositoryId}`} name="file" hidden onChange={handleFileChange} />
                <label htmlFor={`file-${repositoryId}`} className={styles.fileInputField} tabIndex={0} onKeyDown={handleKeyDown}>
                  {fileName ? (
                    <>
                      {fileName}
                      <Cross2Icon className={styles.closeIcon} onClick={handleClearFile} />
                    </>
                  ) : (
                    'Select...'
                  )}
                </label>
              </InputContainer>
            </div>

            <div className={styles.flexContainer}>
              <TextInput
                id={`gitCommitHash-${repositoryId}`}
                name="gitCommitHash"
                label="Git commit hash"
                value={commitHash}
                onChange={handleCommitHashChange}
                placeholder="Ex: a1b2c3d4e5f67890abcdef1234567890abcdef12"
                isOptional
              />
            </div>

            <div className={styles.uploadButtonContainer}>
              <DefaultButton
                buttonType="primary"
                buttonRole="submit"
                state={!selectedTool ? 'disabled' : uploadScanMutation.isPending ? 'loading' : 'default'}
              >
                Upload
              </DefaultButton>
            </div>
          </form>

          <Dialog.Close asChild className={styles.closeButton}>
            <button aria-label="Close" onClick={handleCloseDrawer}>
              <Cross2Icon className={styles.closeButtonIcon} />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

const extractData = (formData: FormData): any => {
  const data: any = {}
  formData.forEach((value, key) => {
    if (value instanceof File) {
      data[key] = value
    } else {
      data[key] = value.toString()
    }
  })
  return data
}

const verifyData = (data: any): CreateScanRequestFormData & { repositoryId: string } =>
  CreateScanRequestFormData.extend({
    repositoryId: z.string(),
  }).parse({
    repositoryId: data.repositoryId,
    metadata: {
      tool: (data.tool as string).toLowerCase(),
      sha: data.gitCommitHash,
    },
    files:
      data.file && data.file.name
        ? {
            [data.file.name]: data.file,
          }
        : undefined,
  })
